import { log } from '@axa-fr-axion/log';
import { FunctionalErrorReason } from '@axa-fr-axion/restful';
import { Adresse, Civilite, Conseiller, Dossier, Funnel, Personne } from '@axa-fr-axion/user-journey-services';
import { OptionData } from '@axafr-btc-ds/form-select';
import { mapAnalyticToProprietes } from '@axa-fr-axion/analytic-cookie';
import { produce } from 'immer';
import merge from 'lodash.merge';
import { AppOptions } from 'App.interface';
import { JourneyData, JourneyDataValidity, JourneyInitialData } from 'data';
import { HomeAddressData, IdentityData } from 'features/contact';
import { GenderOptions } from 'features/contact/identity/gender';
import { NavigationPath, StepNavigationPath, TechNavigationPath } from 'interfaces';
import { PresetDefinition } from 'presets';
import { LogementTypeOptions } from 'features/logement-type';
import { LogementNombrePiecesOptions } from 'features/logement-nombre-pieces';
import { LocataireTypeOptions } from 'features/locataire-type';
import { LogementResidencePrincipaleOptions } from 'features/logement-residence-principale';
import { LogementAdresseData } from 'features/logement-adresse';
import { LogementDebutAssuranceOptions } from 'features/logement-debut-assurance';
import { formatDateForBackend, formatDateFromBackend } from 'services/shared';

export const findAgentLink = 'https://www.findagent.com';
export const DEFAULT_OPTIONS: AppOptions = {
    editorialCommon: {
        axaPartnerPhoneNumber: '0970821010',
        findAdvisorWhenApartFromMetropolisLink: findAgentLink,
        findAdvisorWhenUnreliableCoordinatesLink: findAgentLink,
        findAdvisorWhenRejectedLink: findAgentLink,
    },
    editorialSpecific: {
        csiPhoneNumberEnd: '0969361469',
        csiPhoneNumberHelp: '0967371245',
    },
};

export const EMPTY_JOURNEY_VALIDITY_DATA: JourneyDataValidity = {
    isIdentityValid: false,
    isHomeAddressValid: false,
    isSelectedAdvisorValid: false,
    isLogementTypeValid: false,
    isLogementNombrePiecesValid: false,
    isLocataireTypeValid: false,
    isLogementResidencePrincipaleValid: false,
    isLogementAdresseValid: false,
    isLogementDebutAssuranceValid: false,
};

export const EMPTY_JOURNEY_DATA: JourneyData = {
    ...EMPTY_JOURNEY_VALIDITY_DATA,
    folder: {
        id: undefined,
        lead: {
            id: undefined,
        },
    },
    identity: {
        gender: undefined,
        firstname: undefined,
        lastname: undefined,
        phone: undefined,
        email: undefined,
        birthdate: undefined,
    },
    homeAddress: {
        address: undefined,
        postalCode: undefined,
        city: undefined,
        isOptin: undefined,
    },
};

export const initJourneyDataWithOrigin = () => (
    initialData: Partial<JourneyInitialData> | undefined,
): JourneyData => {
    const initialDataFromOrigin: Partial<JourneyInitialData> = {};

    return merge(
        {},
        EMPTY_JOURNEY_DATA,
        initialData,
        initialDataFromOrigin,
    );
};

export function getInitialStep(
    preset: PresetDefinition
): StepNavigationPath | TechNavigationPath {
    return preset.initialStep;
}

export const folderReady = (journeyData: JourneyData): boolean =>
    Boolean(journeyData.folder.id && journeyData.folder.lead?.id);

export const assignProprietes = (journeyData: JourneyData): Dossier => produce(journeyData.folder, (draft: Dossier) => {
    if (draft.proprietes === undefined) {
        draft.proprietes = [];
    }
    mapLogementTypeDataToProprietes(draft, journeyData.logementType);
    mapLogementNombrePiecesDataToProprietes(draft, journeyData.logementNombrePieces);
    mapLocataireTypeDataToProprietes(draft, journeyData.locataireType);
    mapLogementResidencePrincipaleDataToProprietes(draft, journeyData.logementResidencePrincipale);
    mapLogementAdresseDataToProprietes(draft, journeyData.logementAdresse);
    mapLogementDebutAssuranceDataToProprietes(draft, journeyData.logementDebutAssurance);
});

export function mapLogementTypeDataToProprietes(
    folder: Dossier,
    data: LogementTypeOptions | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'type_bien',
            valeur: data,
        });
    }
    return folder;
}

export function mapLogementNombrePiecesDataToProprietes(
    folder: Dossier,
    data: LogementNombrePiecesOptions | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'nombre_pieces',
            valeur: data,
        });
    }
    return folder;
}

export function mapLocataireTypeDataToProprietes(
    folder: Dossier,
    data: LocataireTypeOptions | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'qualite_assure',
            valeur: data,
        });
    }
    return folder;
}

export function mapLogementResidencePrincipaleDataToProprietes(
    folder: Dossier,
    data: LogementResidencePrincipaleOptions | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'residence_principale',
            valeur: data,
        });
    }
    return folder;
}

export function mapLogementDebutAssuranceDataToProprietes(
    folder: Dossier,
    data: LogementDebutAssuranceOptions | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'date_debut_contrat',
            valeur: data,
        });
    }
    return folder;
}

export function mapLogementAdresseDataToProprietes(
    folder: Dossier,
    data: LogementAdresseData | undefined
): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        const { address, postalCode, city } = data;
        if (address) {
            folder.proprietes.push({
                nom: 'adresseLigne1',
                valeur: address,
            });
        }
        if (postalCode) {
            folder.proprietes.push({
                nom: 'cpDomicile',
                valeur: postalCode,
            });
        }
        if (city) {
            folder.proprietes.push({
                nom: 'villeDomicile',
                valeur: city,
            });
        }
        if (city && address && postalCode) {
            folder.proprietes.push({
                nom: 'adresse_bien',
                valeur: [ address, postalCode, city ].join(' '),
            });
        }
    }
    return folder;
}

export const assignAnalyticsProprietes =
    (journeyData: JourneyData): Dossier => produce(journeyData.folder, (draft) => {
        if (draft.proprietes === undefined) {
            draft.proprietes = [];
        }
        mapAnalyticToProprietes(draft, journeyData.analytic);
    });

export const toCivilite = (gender?: GenderOptions): Civilite | undefined => {
    if (!gender) {
        return undefined;
    }
    switch (gender) {
        case 'ms':
            return 'Mme';
        case 'mr':
            return 'M';
        default:
            throw new Error(`Unknown gender [${ gender }]`);
    }
};

export const toGender = (civilite?: Civilite | string | null): GenderOptions | undefined => {
    switch (civilite) {
        case 'M':
            return 'mr';
        case 'Mme':
            return 'ms';
        default:
            return undefined;
    }
};

export const toAdvisor = (advisor: Conseiller) => {
    const city = [
        advisor.adresse?.codePostal,
        advisor.adresse?.ville,
    ].filter(Boolean).join(' ');
    const streets = [
        advisor.adresse?.adresse,
        advisor.adresse?.[ 'complement-adresse' ],
    ].filter(Boolean) as Array<string>;
    return {
        avatar: advisor.urlPhoto,
        firstName: advisor.prenom,
        lastName: advisor.nom,
        status: advisor.reseau,
        streets,
        city: city || undefined,
        phone: advisor.tel1 || advisor.tel2,
        mail: advisor.email,
        openingHours: advisor.openingHours?.at(0),
        website: advisor.url,
        orias: advisor.orias,
        labelCommercial: advisor.libelleCommercial,
    };
};

export const toPersonne = (identity: IdentityData, address: HomeAddressData): Personne => ({
    nom: identity.lastname,
    prenom: identity.firstname,
    civilite: toCivilite(identity.gender),
    adresse: toAdresse(address),
    dateDeNaissance: formatDateForBackend(identity.birthdate),
    telephone: identity.phone,
    email: identity.email,
    optinAxa: address.isOptin || false,
});

export const toIdentity = (personne: Personne | undefined): IdentityData => ({
    lastname: personne?.nom,
    firstname: personne?.prenom,
    gender: toGender(personne?.civilite),
    phone: personne?.telephone,
    email: personne?.email,
    birthdate: formatDateFromBackend(personne?.dateDeNaissance),
});

const caseMapping: { [key: string]: StepNavigationPath } = {
    'PERSONALISATION': 'End/Confirmation',
    'BANQUE': 'End/Banque',
    'AXAPART': 'End/AxaPartner',
    'CSI': 'End/Csi',
    'HORS-CIBLE': 'End/Reject',
    'FIABILITE': 'End/Unreliable-contact-information',
    'HORS-METROPOLE': 'End/Apart-from-metropolis',
    'HORS-LIMITE': 'End/Out-of-bounds',
    'ASSURANCE': 'End/Insurance',
    'SOLLICITATION': 'End/Sollicitation',
};

export const chooseEndStep = (inCase?: Funnel | FunctionalErrorReason | string): StepNavigationPath => {
    if (inCase && caseMapping[ inCase ]) {
        return caseMapping[ inCase ];
    }
    log.error(`Unknown case ${ inCase }`);
    return 'Error';
};

// NOTE: To be used inside immer js as we mute the draft object inside this function

export function getValue(data: string | OptionData): string {
    if (typeof data === 'string') {
        return data;
    }
    return data.label;
}

export const toAdresse = (address: HomeAddressData): Adresse => ({
    adresse: address.address,
    codePostal: address.postalCode,
    ville: address.city,
    pays: 'FRA',
});

const hasManyAdvisors = (folder: Dossier): boolean => (folder.lead?.conseillers && folder.lead?.conseillers.length > 1)
    || false;
export const selectDefaultAdvisor = (folder: Dossier): Conseiller | undefined => {
    if (hasManyAdvisors(folder)) {
        return undefined;
    }
    return folder.lead?.conseillers?.at(0);
};

export const choosePostContactStep = (folder: Dossier): NavigationPath => {
    if (hasManyAdvisors(folder)) {
        return 'ChooseAdvisor';
    }

    return 'PreConfirmation';
};

export const putAdvisorInLead = (folder: Dossier, advisor?: Conseiller): Dossier => ({
    ...folder,
    lead: {
        ...folder.lead,
        conseillers: advisor ? [ advisor ] : folder.lead?.conseillers,
    },
});
